import cx from 'classnames';
import PropTypes from 'prop-types';

import Image from '@plone/volto/components/theme/Image/Image';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { homepageImageSources } from '../../../helpers/homepageUtils';
import { resolveRichTextHtml } from '../../../helpers/richTextHtml';

const isExternalHref = (href) =>
  href?.startsWith('http://') || href?.startsWith('https://');

const renderDescription = (html, className) => {
  if (!html) {
    return null;
  }

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{
        __html: resolveRichTextHtml(html),
      }}
    />
  );
};

const resolveMedia = (data) => data.image ?? data.icon;

const imagePropsFor = (image, variant) => {
  const icon = variant === 'icon';
  const photo = variant === 'photo';
  const sources = homepageImageSources(image, {
    minWidth: icon ? 64 : photo ? 800 : 400,
    fallback: icon ? 'thumb' : photo ? 'large' : 'preview',
  });
  if (!sources.src) {
    return null;
  }
  return {
    src: sources.src,
    srcSet: sources.srcSet,
    sizes: icon
      ? '64px'
      : photo
        ? '(max-width: 768px) 100vw, 30vw'
        : '(max-width: 768px) 100vw, 400px',
    width: sources.width,
    height: sources.height,
    // Image sets fetchpriority=high on eager images; a card grid must not
    // compete with the page's LCP image.
    fetchpriority: 'auto',
  };
};

export const resolveCardVariant = (data) =>
  data?.variation ||
  data?.variant ||
  (data?.icon && !data?.image ? 'icon' : 'photo');

const resolveVariant = resolveCardVariant;

const IconVariantBody = ({ data, imageProps, imageAlt }) => {
  const { title, description, description_extra: descriptionExtra } = data;

  return (
    <div className="mceTmpl icon-title-text-container">
      <div className="icon-title-container">
        {imageProps ? (
          <Image className="icon" alt={imageAlt} {...imageProps} />
        ) : (
          <div className="csj-card-icon-placeholder" />
        )}
        <div className="icon-item-content">
          {title ? (
            <div className="title">
              <h5>{title}</h5>
            </div>
          ) : null}
          {(description || descriptionExtra) && (
            <div className="text-container">
              {renderDescription(description)}
              {renderDescription(descriptionExtra)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const renderLinkedLabel = (title, link, isEditMode) => {
  if (!title) {
    return null;
  }

  if (link && !isEditMode) {
    return (
      <UniversalLink
        href={link}
        className="csj-card-label-top-link"
        openLinkInNewTab={isExternalHref(link)}
      >
        {title}
      </UniversalLink>
    );
  }

  return <span className="csj-card-label-top-text">{title}</span>;
};

const renderLinkedImage = (imageProps, imageAlt, link, isEditMode) => {
  const image = imageProps ? (
    <Image className="csj-card-label-top-icon" alt={imageAlt} {...imageProps} />
  ) : (
    <div className="csj-card-icon-placeholder" />
  );

  if (link && !isEditMode) {
    return (
      <UniversalLink
        href={link}
        className="csj-card-label-top-image-link"
        openLinkInNewTab={isExternalHref(link)}
      >
        {image}
      </UniversalLink>
    );
  }

  return image;
};

const LabelTopVariantBody = ({ data, imageProps, imageAlt, isEditMode }) => {
  const { title, link } = data;

  return (
    <div className="csj-card-label-top">
      <div className="csj-card-label-top-heading">
        {renderLinkedLabel(title, link, isEditMode)}
      </div>
      <div className="csj-card-label-top-media">
        {renderLinkedImage(imageProps, imageAlt, link, isEditMode)}
      </div>
    </div>
  );
};

const StackedVariantBody = ({ data, imageProps, imageAlt }) => {
  const { title, description, description_extra: descriptionExtra } = data;

  return (
    <div className="csj-card-stacked">
      <div className="csj-card-stacked-media">
        {imageProps ? (
          <Image
            className="csj-card-stacked-icon"
            alt={imageAlt}
            {...imageProps}
          />
        ) : (
          <div className="csj-card-icon-placeholder" />
        )}
      </div>
      <div className="csj-card-stacked-content">
        {title ? <h5 className="csj-card-stacked-title">{title}</h5> : null}
        {renderDescription(description, 'csj-card-stacked-description')}
        {renderDescription(descriptionExtra, 'csj-card-stacked-description')}
      </div>
    </div>
  );
};

const PhotoVariantBody = ({ data, imageProps, imageAlt }) => {
  const { title, description, description_extra: descriptionExtra } = data;

  return (
    <div className="csj-card-photo">
      <div className="csj-card-photo-image">
        {imageProps ? (
          <Image className="ui image" alt={imageAlt} {...imageProps} />
        ) : (
          <div className="csj-card-photo-placeholder" />
        )}
      </div>
      <div className="csj-card-photo-content">
        {title ? <h5 className="csj-card-photo-title">{title}</h5> : null}
        {renderDescription(description, 'csj-card-photo-description')}
        {renderDescription(descriptionExtra, 'csj-card-photo-description')}
      </div>
    </div>
  );
};

const CardBody = ({ data, className, isEditMode = false }) => {
  const media = resolveMedia(data);
  const variant = resolveVariant(data);
  const imageProps = imagePropsFor(media, variant);
  const imageAlt =
    (typeof media === 'object' && !Array.isArray(media) && media?.title) ||
    data.title ||
    '';

  let body = null;
  if (variant === 'stacked') {
    body = (
      <StackedVariantBody
        data={data}
        imageProps={imageProps}
        imageAlt={imageAlt}
      />
    );
  } else if (variant === 'label-top') {
    body = (
      <LabelTopVariantBody
        data={data}
        imageProps={imageProps}
        imageAlt={imageAlt}
        isEditMode={isEditMode}
      />
    );
  } else if (variant === 'icon') {
    body = (
      <IconVariantBody
        data={data}
        imageProps={imageProps}
        imageAlt={imageAlt}
      />
    );
  } else {
    body = (
      <PhotoVariantBody
        data={data}
        imageProps={imageProps}
        imageAlt={imageAlt}
      />
    );
  }

  return (
    <div className={cx('csj-card', `csj-card--${variant}`, className)}>
      {body}
    </div>
  );
};

CardBody.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  className: PropTypes.string,
  isEditMode: PropTypes.bool,
};

export default CardBody;
